import { API } from 'api';
import { deleteCookie, envCookieName, getDomainName, isUndefinedOrNullOrEmpty, setCookie } from 'common';
import { changeCountryCode, changeLanguageCode, setIsLoggedIn, setUserCredential } from 'modules';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import dayjs from 'dayjs';

interface IValidateOtpPayload {
  payload: {
    aid: string;
    email: string;
    otp: string;
    redirectUrl?: string;
  };
}

const validateOtp = (payload: IValidateOtpPayload['payload']) => {
  return API.newPost('/api/admin/pub/validate/otp', payload);
};

export const useValidateOtpData = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [, setExpiredOnCookie] = useCookies([envCookieName('expiredOn')]);
  return useMutation(validateOtp, {
    onSuccess: (data, payload: IValidateOtpPayload['payload']) => {
      const result = data.data;
      dispatch(setUserCredential(result));
      // Set authentication cookies (CRITICAL for Employer Portal)
      if (result.accessToken.length <= 4096) {
        setCookie('accessToken', result.accessToken, result.expiredOn);
      } else {
        // Split access token into chunks
        const CHUNK_SIZE = 2048;
        const tokenChunks: string[] = [];
        for (let i = 0; i < result.accessToken.length; i += CHUNK_SIZE) {
          tokenChunks.push(result.accessToken.slice(i, i + CHUNK_SIZE));
        }
        setCookie('accessTokenChunks', tokenChunks.length.toString(), result.expiredOn);
        tokenChunks.forEach((chunk, index) => {
          setCookie(`accessTokenChunk${index}`, chunk, result.expiredOn);
        });
      }
      deleteCookie('refreshToken');
      setCookie(envCookieName('refreshToken'), result.refreshToken, result.expiredOn);
      // Use react-cookie setter for expiredOn so PublicRoute can read it immediately
      deleteCookie('expiredOn');
      setExpiredOnCookie(envCookieName('expiredOn'), result.expiredOn, {
        domain: getDomainName(),
        expires: dayjs.unix(result.expiredOn).toDate(),
        ...(window.location.protocol === 'https:' && { secure: true }),
      });
      // Set user info cookies
      deleteCookie('employerId');
      setCookie(envCookieName('employerId'), result.eid ?? '');
      // setCookie('employerProfiles', JSON.stringify(result.employers || []));
      setCookie('accountId', result.aid);
      setCookie('permissions', JSON.stringify(result.permissions || []));
      setCookie('email', result.userProfile.email);
      setCookie('firstTimeLogin', result.firstTimeLogin ? 'true' : 'false');

      const country = result.employers?.[0]?.country || 'my';
      const language = localStorage.getItem('i18nextLng') ?? 'en';

      const locale = `${language}-${country.toUpperCase()}`;

      deleteCookie('locale');
      setCookie(envCookieName('locale'), locale);

      if (!isUndefinedOrNullOrEmpty(result.employers) && result.employers.length > 0) {
        dispatch(changeCountryCode({ code: result.employers[0]?.country || 'my' }));
      }
      dispatch(changeLanguageCode(localStorage.getItem('i18nextLng') ?? 'en'));
      dispatch(setIsLoggedIn(true));
      localStorage.removeItem('2fa');

      if (result.firstTimeLogin) {
        history.push('/setup-password');
        return;
      }

      if (!isUndefinedOrNullOrEmpty(result.employers) && result.employers.length > 1) {
        history.push(`/employer-company${history.location.search}`);
        return;
      }

      if (payload.redirectUrl) {
        history.push(payload.redirectUrl);
        return;
      }

      // Only redirect to employer portal if user is an employer admin (has eid)
      if (!isUndefinedOrNullOrEmpty(result.eid)) {
        window.location.href = `${process.env.REACT_APP_EMPLOYER_PORTAL_URL}/dashboard`;
        return;
      }

      // Super admins stay on admin portal
      history.push('/employer/list');
    },
  });
};
